import React, { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { UserContext } from '../ContextProviders';
import { useNovaChat } from '@/hooks/useNovaChat';
import { listNovaDocuments, deleteNovaDocument, NOVA_DOCUMENT_FORMATS_LABEL, NOVA_DOCUMENT_LIMITS_LABEL } from '@/api';
import { useNotification } from '@/components';
import { NovaDocumentObject } from '@/types';
import { useNova } from './NovaContext';
import NovaLandingSidebar from './NovaLandingSidebar';
import NovaUploadDialog from './NovaUploadDialog';
import { novaThemes, novaDisclaimer } from './novaTheme';
import { NovaAttachIcon, NovaUploadIcon, NovaMoreIcon, NovaChevronDownIcon, NovaChevronRightIcon } from './novaIcons';

const NOVA_HOME_HREF = '/tools/nova-ai';

const VISIBLE_ROW_LIMIT = 6;

const extensionOf = (filename: string): string => {
  const dot = filename.lastIndexOf('.');
  if (dot === -1) return 'FILE';
  return filename.slice(dot + 1).toUpperCase().slice(0, 4);
};

const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
};

const formatAddedDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

// The small "paper with a folded corner" file-type glyph from the design handoff —
// a literal document icon rather than a themed one, so it keeps the same white-card
// look in both Nova themes.
const NovaDocumentTypeIcon: React.FC<{ format: string }> = ({ format }) => (
  <span
    className="relative flex h-10 w-8 flex-none items-end justify-center overflow-hidden rounded-[4px] border pb-[5px]"
    style={{ background: '#fff', borderColor: '#E7E4DA' }}
  >
    <span className="text-[8.5px] font-semibold tracking-[.03em]" style={{ color: '#6b6659' }}>
      {format}
    </span>
    <span className="absolute top-0 right-0 h-[9px] w-[9px]" style={{ background: '#EDEAE1', clipPath: 'polygon(0 0, 100% 100%, 100% 0)' }} />
  </span>
);

// The "Documents" full-page state from the Nova buildout handoff (Nova Page
// Documents) — the library of files a user has added from the composer, indexed
// once and reusable across every chat. Reuses the same rail as
// NovaLanding/NovaDiscover/NovaTemplates so all of Nova's full-page states read as
// one product. Uploads go through the same flow as NovaPanel's composer
// (NovaUploadDialog → services/gdai/nova/documents/upload); there's no separate
// metadata database, so a newly uploaded file is picked up by re-fetching the list
// rather than an optimistic local insert.
const NovaDocuments: React.FC = () => {
  const router = useRouter();
  const user = useContext(UserContext);
  const { theme } = useNova();
  const { showNotification } = useNotification();
  const { conversations, conversationsLoading, loadConversations, deleteConversations } = useNovaChat();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [documents, setDocuments] = useState<NovaDocumentObject[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [deletingKeys, setDeletingKeys] = useState<string[]>([]);
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [dialogInitialFiles, setDialogInitialFiles] = useState<File[]>([]);
  const surfaces = novaThemes[theme];

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDocuments = async () => {
    if (!user?.amsUserId || !user?.userId) return;
    setDocumentsLoading(true);
    try {
      const resp = await listNovaDocuments(user.userId, user.amsUserId);
      if (resp.succeeded) {
        setDocuments(resp.documents ?? []);
      } else {
        showNotification(resp.message || 'Could not load your documents.', 'error');
      }
    } catch (error) {
      console.error('Failed to load Nova documents:', error);
      showNotification('Could not load your documents.', 'error');
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.amsUserId, user?.userId]);

  // Closes the open row menu on any click outside it — each row's trigger + menu
  // share a `data-doc-menu-root` wrapper keyed by doc.key, so a click is "inside"
  // only when it lands under the currently-open row's own wrapper.
  useEffect(() => {
    if (!openMenuKey) return;
    const handlePointerDown = (event: MouseEvent) => {
      const menuRoot = (event.target as HTMLElement).closest('[data-doc-menu-root]');
      if (!menuRoot || menuRoot.getAttribute('data-doc-menu-root') !== openMenuKey) {
        setOpenMenuKey(null);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [openMenuKey]);

  const openUploadDialog = (files?: File[]) => {
    setDialogInitialFiles(files || []);
    setUploadDialogOpen(true);
  };

  const handleUploaded = (filePaths: string[]) => {
    setUploadDialogOpen(false);
    if (filePaths.length > 0) fetchDocuments();
  };

  const handleRemoveDocument = async (doc: NovaDocumentObject) => {
    setOpenMenuKey(null);
    setDeletingKeys((prev) => [...prev, doc.key]);
    try {
      const resp = await deleteNovaDocument(user?.userId || '', doc.key);
      if (resp.succeeded) {
        setDocuments((prev) => prev.filter((d) => d.key !== doc.key));
      } else {
        showNotification(resp.message || `Could not remove ${doc.name}.`, 'error');
      }
    } catch (error) {
      console.error('Failed to delete Nova document:', error);
      showNotification(`Could not remove ${doc.name}.`, 'error');
    } finally {
      setDeletingKeys((prev) => prev.filter((key) => key !== doc.key));
    }
  };

  const handleAskAboutDocument = (doc: NovaDocumentObject) => {
    // `file` is the key's trailing filename — what AVA expects in file_paths —
    // so the landing page attaches the document to the seeded question.
    const relativeName = doc.key.split('/').pop() || doc.name;
    router.push(
      `${NOVA_HOME_HREF}?q=${encodeURIComponent(`What can you tell me about ${doc.name}?`)}&file=${encodeURIComponent(relativeName)}`,
    );
  };

  const visibleDocuments = showAll ? documents : documents.slice(0, VISIBLE_ROW_LIMIT);

  return (
    <div
      className="nova-full-page flex h-[calc(100dvh-var(--header-height))] flex-col gap-0 overflow-hidden md:flex-row"
      style={{ background: surfaces.surfaces.panelBase, color: surfaces.ink.body }}
    >
      <NovaLandingSidebar
        theme={theme}
        active="documents"
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed(true)}
        onExpand={() => setSidebarCollapsed(false)}
        onNewChat={() => router.push(NOVA_HOME_HREF)}
        conversations={conversations}
        conversationsLoading={conversationsLoading}
        onSelectConversation={(id) => router.push(`${NOVA_HOME_HREF}?c=${encodeURIComponent(id)}`)}
        onDeleteConversation={(id) => deleteConversations([id])}
        userName={user?.name}
      />

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="relative min-h-0 flex-1 overflow-y-auto" data-lenis-prevent>
          <div className="mx-auto max-w-[1040px] px-4 pt-6 pb-8 sm:px-8 md:px-12 md:pt-11 md:pb-10">
            <div className="mb-2.5 text-xs tracking-[0.14em] uppercase" style={{ color: surfaces.ink.secondary }}>
              Your workspace
            </div>
            <div className="mb-3.5 flex items-end gap-3 md:gap-6">
              <h1
                className="font-sohne text-[28px] leading-[32px] font-semibold tracking-[-0.6px] md:text-[40px] md:leading-[44px] md:tracking-[-1.1px] text-pretty"
                style={{ color: surfaces.ink.heading }}
              >
                Documents
              </h1>
              <span className="flex-1" />
              <button
                type="button"
                onClick={() => openUploadDialog()}
                className="inline-flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-full px-4 text-sm font-semibold md:h-[42px] md:px-[18px] md:text-[15px]"
                style={{ background: surfaces.actionAccent, color: surfaces.ink.onGold }}
              >
                <NovaAttachIcon className="h-4 w-4" />
                Add document
              </button>
            </div>
            <p className="mb-6 max-w-[640px] text-base leading-[24px] text-pretty md:mb-[30px] md:text-lg md:leading-[27px]" style={{ color: surfaces.ink.body }}>
              Files you add here are indexed once and available to every question you ask — Nova.ai answers from them
              alongside the Stylus library, and cites both.
            </p>

            <div
              role="button"
              tabIndex={0}
              onClick={() => openUploadDialog()}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openUploadDialog();
                }
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setIsDragOver(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragOver(false);
                if (event.dataTransfer.files.length > 0) openUploadDialog(Array.from(event.dataTransfer.files));
              }}
              className="mb-6 flex cursor-pointer items-center gap-4 rounded-2xl border border-dashed p-4 md:mb-[34px] md:p-[22px]"
              style={{
                borderColor: isDragOver ? surfaces.actionAccent : surfaces.surfaces.panelBorder,
                background: surfaces.surfaces.field,
              }}
            >
              <span
                className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[11px]"
                style={{ background: surfaces.surfaces.activeRow, color: surfaces.ink.heading }}
              >
                <NovaUploadIcon className="h-[18px] w-[18px]" />
              </span>
              <span className="flex flex-col gap-0.5">
                <span className="text-[15px] font-semibold" style={{ color: surfaces.ink.heading }}>
                  Drop files here, or browse your computer
                </span>
                <span className="text-[13.5px]" style={{ color: surfaces.ink.secondary }}>
                  {NOVA_DOCUMENT_FORMATS_LABEL} · {NOVA_DOCUMENT_LIMITS_LABEL} · private to you
                </span>
              </span>
            </div>

            <div className="mb-3 flex items-center gap-3.5">
              <span className="text-[11px] tracking-[0.14em] uppercase" style={{ color: surfaces.ink.secondary }}>
                {documentsLoading ? 'Loading…' : `${documents.length} document${documents.length === 1 ? '' : 's'}`}
              </span>
              <span className="flex-1" />
              <span className="inline-flex items-center gap-1.5 text-[13.5px]" style={{ color: surfaces.ink.secondary }}>
                Sort: <span className="font-medium" style={{ color: surfaces.ink.heading }}>Recently added</span>
                <NovaChevronDownIcon className="h-3.5 w-3.5" />
              </span>
            </div>

            <div
              className="grid grid-cols-[1fr_auto] gap-3 md:grid-cols-[1fr_180px] md:gap-5 border-b px-1 pb-2.5 text-[11.5px] tracking-[0.08em] uppercase"
              style={{ borderColor: surfaces.surfaces.panelBorder, color: surfaces.ink.placeholder }}
            >
              <span>Name</span>
              <span />
            </div>

            <div className="flex flex-col">
              {!documentsLoading && documents.length === 0 && (
                <div className="py-8 text-center text-[14px]" style={{ color: surfaces.ink.secondary }}>
                  No documents yet — add one above to get started.
                </div>
              )}
              {visibleDocuments.map((doc) => {
                const format = extensionOf(doc.name);
                const isDeleting = deletingKeys.includes(doc.key);
                const isMenuOpen = openMenuKey === doc.key;
                return (
                  <div
                    key={doc.key}
                    className="grid grid-cols-[1fr_180px] items-center gap-5 border-b px-1 py-4"
                    style={{ borderColor: surfaces.surfaces.hairline }}
                  >
                    <div className="flex min-w-0 items-center gap-3.5">
                      <NovaDocumentTypeIcon format={format} />
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="truncate text-base font-medium" style={{ color: surfaces.ink.heading }}>
                          {doc.name}
                        </span>
                        <span className="text-[13px]" style={{ color: surfaces.ink.placeholder }}>
                          {formatBytes(doc.size)} · added {formatAddedDate(doc.lastModified)}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleAskAboutDocument(doc)}
                        className="inline-flex h-8 cursor-pointer items-center rounded-full border px-3.5 text-[13.5px] font-medium whitespace-nowrap"
                        style={{ borderColor: surfaces.surfaces.panelBorder, background: surfaces.surfaces.card, color: surfaces.ink.heading }}
                      >
                        Ask Nova
                      </button>
                      <div className="relative" data-doc-menu-root={doc.key}>
                        <button
                          type="button"
                          aria-label={`More actions for ${doc.name}`}
                          aria-haspopup="menu"
                          aria-expanded={isMenuOpen}
                          onClick={() => setOpenMenuKey((prev) => (prev === doc.key ? null : doc.key))}
                          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full"
                          style={{ color: surfaces.ink.placeholder, background: isMenuOpen ? surfaces.surfaces.activeRow : 'transparent' }}
                        >
                          <NovaMoreIcon className="h-4 w-4" />
                        </button>
                        {isMenuOpen && (
                          <div
                            role="menu"
                            className="absolute top-9 right-0 z-10 w-40 overflow-hidden rounded-xl border py-1 shadow-lg"
                            style={{ borderColor: surfaces.surfaces.panelBorder, background: surfaces.surfaces.card }}
                          >
                            <button
                              type="button"
                              role="menuitem"
                              disabled={isDeleting}
                              onClick={() => handleRemoveDocument(doc)}
                              className="block w-full cursor-pointer px-3.5 py-2 text-left text-[13.5px] font-medium text-red-600 disabled:cursor-default disabled:opacity-50"
                            >
                              {isDeleting ? 'Removing…' : 'Delete'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {documents.length > VISIBLE_ROW_LIMIT && (
              <button
                type="button"
                onClick={() => setShowAll((prev) => !prev)}
                className="mt-[18px] flex cursor-pointer items-center gap-2 border-none bg-transparent p-0 text-sm font-medium"
                style={{ color: surfaces.ink.heading }}
              >
                {showAll ? 'Show fewer' : `Show all ${documents.length}`}
                <NovaChevronRightIcon className={`h-3.5 w-3.5 ${showAll ? '-rotate-90' : 'rotate-90'}`} />
              </button>
            )}
          </div>
        </div>

        <div
          className="shrink-0 px-4 pt-2 pb-[18px] text-center text-xs md:text-sm"
          style={{ color: surfaces.ink.placeholder }}
        >
          {novaDisclaimer.short}
        </div>
      </div>

      <NovaUploadDialog
        open={uploadDialogOpen}
        userProfileId={user?.amsUserId || ''}
        initialFiles={dialogInitialFiles}
        onClose={() => setUploadDialogOpen(false)}
        onUploaded={handleUploaded}
      />
    </div>
  );
};

export default NovaDocuments;
